import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import {
    CashTransactionType,
    CardTransactionType,
    EventCategory,
    EventType,
    OperationalError,
    PayStationDeviceType,
    PayStationStage,
    PayStationStatus,
    PayStationOperationType,
} from 'src/features/payment/utils/constants';
import { CURRENCY, TranslationsWithId } from 'src/types';
import { LOCALE } from 'src/providers/types';

export enum PAYMENT_TYPE {
    CARD = 'card',
    CASH = 'cash',
    ONLINE = 'online',
    BANK = 'bank',
}

export enum PayStationPaymentType {
    CARD = 'card',
    CASH = 'cash',
    ONLINE = 'online',
}

export type PayStationActiveOperation = {
    operation_type: PayStationOperationType;
    has_active_operation: boolean;
};

export enum PAYMENT_STATUS {
    PAID = 'paid',
    CANCELLED = 'cancelled',
    FAILED = 'failed',
    REFUNDED = 'refunded',
    ROLLBACKED = 'rollbacked',
}

export enum PAYMENT_SOURCE_TYPE {
    PAY_STATION = 'pay-station',
    ONLINE = 'online',
    OPERATOR = 'operator',
}

export enum PAYMENT_SERVICE_TYPE {
    PARKING = 'parking',
    SUBSCRIPTION = 'subscription',
}
export enum PAYMENT_ITEM_TYPE {
    ACTIVE_STAY = 'active_stay',
    PAST_STAY = 'past_stay',
    MEMBERSHIP = 'membership',
    RESERVATION = 'reservation',
    SUBSCRIPTION = 'subscription',
}

export type Payment = {
    id: string;
    tenant: {
        id: string;
        name: string;
    };
    customer: {
        id: string;
        name: string;
    };
    created_at: string;
    modified_at: string;
    date: string;
    currency: CURRENCY;
    amount: number;
    type: PAYMENT_TYPE;
    reference: string | null;
    //this shape of payment_payload_details is only for the payments table
    payment_payload_details: {
        item_id: string;
        item_type: PAYMENT_ITEM_TYPE;
        item_display: string;
        service_type: PAYMENT_SERVICE_TYPE;
        service_type_display: string;
        payment_source_id: string;
        payment_source_display: string;
        payment_source_type: PAYMENT_SOURCE_TYPE;
        visitor_id: string;
        visitor_display: string;
        entity_id: string;
        entity_display: string;
        membership_category: MEMBERSHIP_TEMPLATE_CATEGORY;
        receipt_number: string | null;
        financial_document_number: string | null;
        pay_station_operation_id?: string | null;
        pay_station_operation_ref?: string | null;
    };
    status: PAYMENT_STATUS;
    refunded_amount: number | null;
    money_account: string;
};

export enum PAY_STATION_STATUS {
    OPERATIONAL = 'operational',
    WARNING = 'warning',
    ERROR = 'error',
    NO_CONNECTION = 'no_connection',
}

export enum PayStationEventAPI {
    CASH_API = 'cash_api',
    CARD_API = 'card_api',
    FISCAL_API = 'fiscal_api',
    BACKEND = 'backend',
}

export type PayStationNominal = {
    currency: CURRENCY;
    value: string;
    enable: boolean;
};

export enum PAY_STATION_UI_MODAL {
    INTERACTIVE = 'interactive',
    UNATTENDED = 'unattended',
    READ_ONLY_DISPLAY = 'readonly_display',
}

export enum PIPELINE_PROFILE {
    BG_WITH_FISCALISATION = 'bg_with_fiscalisation',
    BG_WITHOUT_FISCALISATION = 'bg_without_fiscalisation',
}

export type PayStation = {
    id: string;
    created_at: string;
    modified_at: string;
    name: string;
    code: string;
    status: PAY_STATION_STATUS;
    settings: {
        web_id: string;
        password: string;
        languages: LOCALE[];
        lost_ticket: boolean;
        home_qr_value: string;
        support_phone: string;
        enable_free_stay: boolean;
        default_language: LOCALE;
        home_pay_button_text?: {
            [LOCALE.BG]?: string;
            [LOCALE.EN]: string;
        };
        enable_subscriptions: boolean;
        enable_settings_enter: boolean;
        coin_selector: {
            accepted_nominals: PayStationNominal[];
            payout_nominals: PayStationNominal[];
        };
        banknote_validator: {
            accepted_nominals: PayStationNominal[];
            payout_nominals: PayStationNominal[];
        };
        enabled_payment_methods: PayStationPaymentType[];
        currencies: CURRENCY[];
        pipeline_profile: PIPELINE_PROFILE;
    };
    site: TranslationsWithId;
    api_key: string | null;
    ui_mode: PAY_STATION_UI_MODAL;
    backend_url: string;
    backend_api_key: string;
    gateway: TranslationsWithId;
};

export type PayStationOperation = {
    created_at: string;
    pay_station: {
        name: string;
        id: string;
    };
    id: string;
    type: PayStationOperationType;
    ref: string;
    active_stay?: {
        id: string;
        ref: string;
    };
    requested_amount: number;
    paid_amount: number;
    returned_amount: number;
    remaining_amount_to_return: number;
    currency?: string;
    device_type?: PayStationDeviceType;
    stage: PayStationStage;
    status: PayStationStatus;
    past_stay?: {
        id: string;
        ref: string;
    };
};

export enum MetaDeviceType {
    BANKNOTE_VALIDATOR = 'BanknoteValidator',
    COIN_SELECTOR = 'CoinSelector',
    DONGLE = 'Dongle',
    COIN_PAYOUT = 'CoinPayout',
    POS_TERMINAL = 'PosTerminal',
    NONE = 'None',
}

export enum EventMetaType {
    SUCCESS = 'success',
    FAIL = 'fail',
    REVERSE = 'reverse',
    REVERSE_FAIL = 'reverse-fail',
    CANCEL = 'cancel',
}

export type EventMeta1 = {
    event_objects: {
        Count?: number;
        Value?: number;
        Currency?: CURRENCY;
        DeviceType: number;
        InRecycleBox?: boolean;
        ReferenceNumber?: string;
    }[];
};

export type EventMeta2 = {
    address?: string;
    device_type: MetaDeviceType;
    operational_errors: OperationalError[];
};

export type EventMeta4 = {
    error_details:
        | {
              status_code: string;
              name?: string;
              default_code: string;
              default_detail: string;
          }
        | string;
};

export type EventMeta6 = {
    count: number;
    denomination: string;
    currency: CURRENCY;
    in_recycle_box: boolean;
};

export type EventMeta7 = {
    transaction_number: string;
    type: EventMetaType;
    amount: number;
    currency: CURRENCY;
};

export type EventMetaCorrection = {
    currency: CURRENCY;
    // The banknote denomination in major units (e.g. "10" for a 10 EUR note), as sent by the device.
    value: string;
    old_count: number;
    new_count: number;
};

// SYNCHRONIZE_NOMINAL_CORRECTED_FROM_DEVICE (62): the device-corrected recycle-box levels.
// `corrections` carries the tracked before/after counts; `event_objects` is the raw device snapshot.
export type EventMetaCorrections = {
    corrections?: EventMetaCorrection[];
    event_objects?: EventMeta1['event_objects'];
};

export type PayStationEvent = {
    id: string;
    timestamp: number;
    meta: EventMeta1 | EventMeta2 | EventMeta4 | EventMeta6 | EventMeta7 | EventMetaCorrections;
    pay_station: {
        id: string;
        name: string;
    };
    paystation_operation?: {
        id: string;
        ref: string;
        type: PayStationOperationType;
        status: PayStationStatus;
    };
    created_at: string;
    modified_at: string;
    type: EventType;
    category: EventCategory;
    received_from: PayStationEventAPI;
};

export type PayStationCashLevel = {
    id: string;
    total_amount: number;
    create_at: string;
    modifier_at: string;
    currency: CURRENCY;
    device_type: number;
    in_recycle_box: boolean;
    value: string;
    count: number;
    pay_station: string;
};

export type PayStationCashTransaction = {
    id: string;
    created_at: string;
    modified_at: string;
    type: CashTransactionType;
    timestamp: string;
    currency: CURRENCY;
    device_type: PayStationDeviceType;
    in_recycle_box: boolean;
    denomination: string;
    count: number;
    amount: number;
    operation: string;
};

export type PayStationCardTransaction = {
    id: string;
    created_at: string;
    modified_at: string;
    type: CardTransactionType;
    timestamp: string;
    transaction_number?: string;
    currency: CURRENCY;
    amount: number;
    operation: string;
    meta?: any;
};

export type PayStationRecycleBoxTransfer = {
    id: string;
    created_at: string;
    modified_at: string;
    timestamp: string;
    currency: CURRENCY;
    denomination: string;
    count: number;
    amount: number;
    operation: string;
};
